/* eslint-disable react/no-find-dom-node */
import * as React from "react"
import * as ReactDOM from "react-dom"

import { Action } from "@tenforce/toolbox-button-push-react"
import { Symbols } from "@tenforce/toolbox-fontmap"

import Term from "./Term"

interface Props {
    isContentShown: boolean
    target: Element
    dictionarySources: Models.ContextualHelpDictionary[]
    toggleTooltip: (
        element: Element | null,
        term: Models.ContextualHelpTerm,
    ) => void
}

interface State {
    contentLeft: string
    contentTop: string
    leftOffset: string
    topOffset: string
    selectedDictionarySource: Models.ContextualHelpDictionary
}

export default class Tooltip extends React.Component<Props, State> {
    content: React.RefObject<HTMLDivElement>
    tooltipAction: React.RefObject<HTMLButtonElement>

    constructor(props: Props) {
        super(props)

        this.state = {
            contentLeft: "0",
            contentTop: "0",
            leftOffset: "0",
            topOffset: "0",
            selectedDictionarySource: props.dictionarySources[0],
        }

        this.content = React.createRef()
        this.tooltipAction = React.createRef()
    }

    onSizeChanged = (): void => {
        this.positionTooltip()
    }

    positionTooltip(): void {
        const { target } = this.props

        // reset positions to make sure getBoundingClientRect will give us the good positions
        this.setState(
            {
                contentLeft: "0",
                contentTop: "0",
                leftOffset: "0",
                topOffset: "0",
            },
            () => {
                let tooltipAction = !!this.tooltipAction.current
                    ? ReactDOM.findDOMNode(this.tooltipAction.current)
                    : null

                if (tooltipAction instanceof Text) {
                    tooltipAction = null
                }

                if (!!target && this.content.current && !!tooltipAction) {
                    const targetBoundingRect = target.getBoundingClientRect()
                    const contentBoundingRect =
                        this.content.current.getBoundingClientRect()
                    const tooltipActionBoundingRect =
                        tooltipAction.getBoundingClientRect()

                    const targetStyle = window.getComputedStyle(target)
                    const targetTopPadding = parseFloat(targetStyle.paddingTop)
                    const targetRightPadding = parseFloat(
                        targetStyle.paddingRight,
                    )

                    // position the tooltip to the top right of the target element without padding
                    let left = targetBoundingRect.right - targetRightPadding

                    if (
                        left + tooltipActionBoundingRect.width >
                        window.innerWidth
                    ) {
                        left -=
                            left +
                            tooltipActionBoundingRect.width -
                            window.innerWidth
                    }

                    let top =
                        scrollY +
                        targetBoundingRect.top -
                        tooltipActionBoundingRect.height +
                        targetTopPadding

                    // make sure that that the tooltip is still in view
                    if (top < 0) {
                        top = 0
                    }

                    // position the content to have the tooltip in the top left corner
                    let contentLeft = 0
                    let contentTop = 0

                    // if there is not enough place on the right of the tooltip
                    // then position the tooltip to the right corner of the content
                    if (left + contentBoundingRect.width > window.innerWidth) {
                        contentLeft = -contentBoundingRect.width
                    }

                    // if there is not enough place under the tooltip
                    // and there is enough place above the target for the content
                    // then position the tooltip to the bottom corner of the content
                    if (
                        top +
                            tooltipActionBoundingRect.height / 2 +
                            contentBoundingRect.height >
                            window.innerHeight &&
                        targetBoundingRect.top - contentBoundingRect.height >= 0
                    ) {
                        contentTop -= contentBoundingRect.height
                    }

                    this.setState({
                        contentLeft: `${contentLeft}px`,
                        contentTop: `${contentTop}px`,
                        leftOffset: `${left}px`,
                        topOffset: `${top}px`,
                    })
                }
            },
        )
    }

    componentDidMount(): void {
        window.addEventListener("resize", this.onSizeChanged)
        window.addEventListener("scroll", this.onSizeChanged)
        this.onSizeChanged()
    }

    componentWillUnmount(): void {
        window.removeEventListener("resize", this.onSizeChanged)
        window.removeEventListener("scroll", this.onSizeChanged)
    }

    render(): JSX.Element {
        const { isContentShown, dictionarySources } = this.props
        const {
            contentLeft,
            contentTop,
            selectedDictionarySource,
            leftOffset,
            topOffset,
        } = this.state

        const terms = dictionarySources.find(
            (dS) => dS.code === selectedDictionarySource.code,
        )?.terms

        if (!terms || terms.length === 0) {
            return <></>
        }

        return (
            <div
                className={isContentShown ? "tw-z-1" : ""}
                style={{
                    left: leftOffset,
                    position: "absolute",
                    top: topOffset,
                }}
                data-ft-component="tooltip"
                data-ft-dictionary-source-count={dictionarySources.length}
                data-ft-is-content-shown={isContentShown}
                data-ft-selected-dictionary-code={selectedDictionarySource.code}
                data-ft-term-count={terms.length}
            >
                <div
                    className={`tw-m-3 bg-default tw-min-w-1/2-screen sm:tw-min-w-1/3-screen ${
                        isContentShown
                            ? "tw-opacity-100"
                            : "tw-opacity-0 tw-pointer-events-none"
                    }`}
                    data-ft-component="tooltip-content"
                    ref={this.content}
                    style={{
                        position: "absolute",
                        left: contentLeft,
                        top: contentTop,
                    }}
                >
                    {terms.map(
                        (term, index): JSX.Element => (
                            <Term
                                key={term.code}
                                shouldShowSeparator={index > 0}
                                term={term}
                            />
                        ),
                    )}
                </div>

                <Action
                    data-ft-component="tooltip-trigger"
                    extraClasses="bg-white tw-rounded-full tw-relative"
                    onClick={this.onClick}
                    ref={this.tooltipAction}
                >
                    {Symbols.Tooltip}
                </Action>
            </div>
        )
    }

    onClick = (): void => {
        const { isContentShown, target, toggleTooltip, dictionarySources } =
            this.props
        toggleTooltip(
            isContentShown ? null : target,
            dictionarySources[0].terms[0],
        )
    }
}
